/**
 * 69.10: the lighter's strip — its site behind the motor pool (step 1), placed at the start with the switch, and the robots'
 * carry from it to the pool (step 3), worked out once and kept on the strip's record. Apart from core/lighter.ts (which sits
 * under the ship body in the import order: this module reaches the motor pool, the base templates and the shapes).
 */
import type { GameState, Vec3 } from '../types';
import { LIGHTER, LIGHTER_UNLOAD } from '../constants/action';
import { MODULE_CELL } from '../constants/modules';
import { PHYS_DT } from '../constants/physics';
import { MOTOR_POOL, motorPoolSite } from './motorPool';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { footprintGround, terraceEdit } from './terraces';
import { TERRACE } from './terraceGround';
import { applyTerrainEdit } from './terrainEdits';
import type { Terrace } from '../types/ground';
import { vecCross } from '../math';
import { dacos, dcos, dsin } from './dmath';
import { vecAdd, vecDot, vecLength, vecNormalize, vecScale, vecSub } from '../math';
import { baseStoreSpot } from './buildCosts';

/**
 * Where a side's strip is: behind its motor pool (or where the pool would be), on the side away from the enemy, the strip's
 * length and a margin further back — and **on the flattest ground there** (the owner: the strip "is curved / bent"): the
 * spot straight back and its neighbours round the base at the same distance (`STRIP_SEARCH.bearings`, either side) and a
 * ring farther out, each footprint's natural rise read as the base planner reads a pad's (`footprintGround`), the least rise
 * taken (ties: the nearest to straight back). `dir` its middle, `front` toward the base. Null without a base to be behind.
 */
const STRIP_SEARCH = { bearings: [0, 0.35, -0.35, 0.7, -0.7, 1.05, -1.05, 1.4, -1.4] as const, rings: [0, 25, 50] as const };
export function lighterStripSite(state: Pick<GameState, 'structures' | 'players' | 'board' | 'actionHomes' | 'planets' | 'config' | 'motorPools'>, team: number): { planetIndex: number; dir: Vec3; front: Vec3; rise: number } | null {
  // (motorPoolSite lays a yard of `rows` cells deep behind the side's reach; a yard the pool's depth plus the strip's and a gap puts its middle where the strip goes)
  const rows = MOTOR_POOL.rows + Math.ceil((LIGHTER.strip.alongPeople + 8) / MODULE_CELL) * 2;
  const site = motorPoolSite(state, team, { rows });
  if (!site) return null;
  const planet = state.planets[site.planetIndex], ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k, R = planet.radius;
  const home = baseStoreSpot(state, team);
  const centre = vecNormalize(site.dir), hq = home && home.planetIndex === site.planetIndex ? vecNormalize(home.surfaceDir) : null;
  const halfX = (LIGHTER.strip.acrossPeople / 2) * person, halfZ = (LIGHTER.strip.alongPeople / 2) * person;
  // (63.16 (7): the ground without the stands — a neighbour's deck and skirt (the motor pool compound's, 17 people half) is not ground to lay a strip on)
  const riseAt = (dir: Vec3, front: Vec3): number => footprintGround(planet, dir, front, vecNormalize(vecCross(front, dir)), halfX, halfZ, ps, 'ground').rise;   // (the ground without stands: 71 step 0's level, not a copied planet)
  let best = { dir: centre, front: vecNormalize(site.front), rise: riseAt(centre, vecNormalize(site.front)), order: 0 };
  if (hq) {
    // the candidates: the great circle from the base out to the centre, turned about the base, at the centre's arc and a ring farther
    const t0 = vecSub(centre, vecScale(hq, vecDot(hq, centre))), arc = dacos(Math.max(-1, Math.min(1, vecDot(hq, centre)))) * R;
    if (vecLength(t0) > 1e-9) {
      const t = vecNormalize(t0), side = vecNormalize(vecCross(hq, t));
      let order = 0;
      for (const ring of STRIP_SEARCH.rings) for (const b of STRIP_SEARCH.bearings) {
        order++;
        const tb = vecNormalize(vecAdd(vecScale(t, dcos(b)), vecScale(side, dsin(b))));
        const u = (arc + ring * person) / R, dir = vecNormalize(vecAdd(vecScale(hq, dcos(u)), vecScale(tb, dsin(u))));
        const front = vecNormalize(vecSub(vecScale(hq, dsin(u)), vecScale(tb, dcos(u))));   // (−d/du of dir: back toward the base)
        const rise = riseAt(dir, front);
        if (rise < best.rise - 1e-9) best = { dir, front, rise, order };
      }
    }
  }
  return { planetIndex: site.planetIndex, dir: best.dir, front: best.front, rise: best.rise };
}

/**
 * 69.10 step 3: the carry from the strip's apron to the pool (seconds) — the robots' pick, the walk over the distance from the
 * strip's middle to the pool's site (or the pool's place), and the stacker's lift.
 */
export function carrySeconds(state: Pick<GameState, 'planets' | 'config' | 'lighterStrips' | 'motorPools' | 'structures' | 'players' | 'board' | 'actionHomes'>, team: number): number {
  const strip = state.lighterStrips?.[team];
  const pool = state.motorPools?.[team] ?? motorPoolSite(state, team);
  if (!strip || !pool) return LIGHTER_UNLOAD.pickSeconds + LIGHTER_UNLOAD.stackSeconds;
  const planet = state.planets[strip.planetIndex], person = PERSON_BODY_HEIGHT * personSetOf(state).k;
  const dist = dacos(Math.max(-1, Math.min(1, vecDot(vecNormalize(strip.dir), vecNormalize(pool.dir))))) * planet.radius / person;
  return LIGHTER_UNLOAD.pickSeconds + dist / LIGHTER_UNLOAD.botSpeedPeople + LIGHTER_UNLOAD.stackSeconds;
}

/** At the start, with the switch: each side's strip (the sides with people). */
export function withLighterStrips(state: GameState): GameState {
  if (!state.config.lighters) return state;
  const strips: NonNullable<GameState['lighterStrips']> = {};
  let planets = state.planets, edits = state.terrainEdits ?? [], laid = false;
  let nextId = 1;
  for (const p of planets) for (const t of p.terraces ?? []) nextId = Math.max(nextId, t.id + 1);
  const ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k;
  for (const team of [...new Set(state.players.map(p => p.teamIndex))].sort((a, b) => a - b)) {
    const site = lighterStripSite({ ...state, planets }, team);
    if (!site) continue;
    const { rise: _r, ...siteOnly } = site;
    let strip: NonNullable<GameState['lighterStrips']>[number] = { ...siteOnly, carryTicks: Math.max(1, Math.round(carrySeconds({ ...state, lighterStrips: { [team]: siteOnly } }, team) / PHYS_DT)) };   // (step 3: the robots' carry from this strip to the pool)
    // the deck (the owner: the strip "is curved / bent"): a terrace under the strip's footprint at the median of the ground, like the base planner's under a pad — a level slab the pose lands on; laid with the fine ground, when the natural rise is within the strip's step
    const planet = planets[site.planetIndex];
    if (planet && planet.type === 'planet' && planet.alive && ps.fineAmplitude > 0) {
      const up = vecNormalize(site.dir), tz = vecNormalize(site.front), across = vecNormalize(vecCross(tz, up));
      const halfX = (LIGHTER.strip.acrossPeople / 2) * person, halfZ = (LIGHTER.strip.alongPeople / 2) * person;
      const { rise, level } = footprintGround(planet, up, tz, across, halfX, halfZ, ps);
      if (rise <= LIGHTER.strip.maxStepPeople * person) {   // (the flattest spot round the base was taken: past the step the strip lies on the ground)
        const t: Terrace = { id: nextId++, dir: up, facing: tz, halfX, halfZ, band: TERRACE.bandPeople * person, level };
        const e = terraceEdit(site.planetIndex, planet, t);
        planets = planets.map((p, i) => (i === site.planetIndex ? applyTerrainEdit(p, e) : p));
        edits = [...edits, e]; laid = true;
        strip = { ...strip, deck: t.id };
      }
    }
    strips[team] = strip;
  }
  if (!Object.keys(strips).length) return state;
  return { ...state, lighterStrips: strips, ...(laid ? { planets, terrainEdits: edits } : {}) };
}
